import { DecimalPipe } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { LsqApiServiceListSource, Pagination } from '@lsq-frontend/angular-shared/api-service';
import {
    ClaimService,
    Color,
    FeatureFlagService,
    ISelectOption,
    Icons,
    Interaction,
    LifecycleAware,
    LsqBarDataPoint,
    LsqDatePipe,
    ResponsiveService,
    StatusColor,
    TableAppearance,
    WindowReferenceService,
    forLifeOf,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    ignoreNullish
} from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ACCOUNT_SUPPLIER_DETAILS,
    BUYER_SUPPLIER_DETAILS,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    ListSellerSummaryForBuyerAccountRequest,
    SellerSummaryEnrollmentStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Observable } from 'rxjs';
import { debounceTime, map, take } from 'rxjs/operators';
import { SupplierStatusDisplay } from '../../supply-chain-direct.models';
import { SupplyChainDirectAccountService } from '../supply-chain-direct-account.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain-direct-buyer-account-shared.service';
import { SuppliersDataSourceService } from './suppliers-data-source.service';

import {
    ListSellerSummaryForBuyerAccountResponse,
    SupplierOverviewDataSource,
    SupplierOverviewDataSourceFactory
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    ListSellerSummaryExportService,
    ListSellerSummaryForBuyerAccountExportService
} from '@lsq-frontend/remote-services/report-center';

@LifecycleAware()
@Component({
    selector: 'supply-chain-direct-account-suppliers',
    template: `
        <business-units-select-page-header></business-units-select-page-header>
        <div class="container-fluid page-container">
            <lsq-data-point-bar
                [loading]="supplierSummaryDataSource.loading$ | async | notNull"
                [dataPoints]="supplierSummaryDataPoints$ | async | notNull"
                [isLeftAligned]="false"
            ></lsq-data-point-bar>
            <card
                class="suppliers-table-card"
                *ngIf="!(responsiveService.mdBelow | async) && !!listSuppliersDataSource"
            >
                <div right-header-content>
                    <form class="suppliers-filters" [formGroup]="filtersFormGroup">
                        <div class="suppliers-filter suppliers-filters-seller-name-filter">
                            <lsq-input
                                id="seller-name-filter"
                                [placeholder]="searchSuppliersPlaceholderI18n"
                                formControlName="sellerNameFilter"
                                [name]="'sellerNameFilter'"
                                [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                                [group]="filtersFormGroup"
                            >
                            </lsq-input>
                        </div>
                        <div
                            class="suppliers-filter suppliers-filters-seller-enrollment-status-filter"
                        >
                            <lsq-multi-select
                                id="seller-enrollment-status-filter"
                                [options]="sellerEnrollmentStatusOptions"
                                [formControlName]="'sellerEnrollmentStatusFilter'"
                                [group]="filtersFormGroup"
                                [selectAllText]="allEnrollmentStatusesSelectedI18n"
                                [placeholder]="enrollmentStatusMultiSelectPlaceholderI18n"
                                [hideLabel]="true"
                                [initializeWithAllSelected]="true"
                            >
                            </lsq-multi-select>
                        </div>
                    </form>
                    <span class="export-button" *ngIf="!(responsiveService.mdBelow | async)">
                        <lsq-export-button
                            [request]="listSuppliersDataSource.request"
                            [service]="
                                isFinancingAccountPortalUser
                                    ? listSellerSummaryExportService
                                    : listSellerSummaryForBuyerExportService
                            "
                            [reportName]="reportName"
                            [isIcon]="true"
                            [disabled]="
                                (listSuppliersDataSource.rows$ | async)?.length == 0 ||
                                    (listSuppliersDataSource.loading$ | async) | notNull
                            "
                        ></lsq-export-button>
                    </span>
                </div>

                <div card-body class="invoice-table">
                    <lsq-data-table
                        *ngIf="!(responsiveService.mdBelow | async)"
                        [dataSource]="listSuppliersDataSource"
                        [layoutFixed]="true"
                        [isPaginated]="true"
                        [appearance]="tableAppearance.Default"
                        [columnVisibilities]="{
                            buyerName: isFinancingAccountPortalUser,
                            feeShare: buyerPaidEarlyPaymentFeePercentageVisible,
                            flexTermAmount: buyerAccountSharedService.isPayLaterEnabled$ | async,
                            flexTermPercentUsed:
                                buyerAccountSharedService.isPayLaterEnabled$ | async
                        }"
                    >
                        <lsq-column
                            [column]="{
                                header: businessUnitI8n,
                                key: 'buyerName',
                                truncateTextCss: true,
                                sortable: true,
                                width: '35%',
                                className: 'first-column'
                            }"
                        >
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: supplierIdI8n,
                                key: 'sellerId',
                                sortable: true,
                                width: '30%',
                                truncateAfter: supplierIdTruncateAfter$ | async | notNull,
                                reverseTruncate: true
                            }"
                        >
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: supplierNameI18n,
                                key: 'name',
                                truncateTextCss: true,
                                sortable: true,
                                width: '35%'
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <div class="text-truncate" [style.maxWidth]="'35rem'">
                                    <a
                                        *ngIf="supplierDetailsIsOn$ | async"
                                        class="a not-bold supplier-name"
                                        [routerLink]="getSupplierDetailsLink(row.sellerKey)"
                                        queryParamsHandling="preserve"
                                    >
                                        {{ row.name }}
                                    </a>
                                    <span *ngIf="!(supplierDetailsIsOn$ | async)">
                                        {{ row.name }}
                                    </span>
                                </div>
                            </ng-template>
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: feeShareI18n,
                                key: 'feeShare',
                                headerPopoverContent: feeSharePopoverI18n,
                                sortable: true,
                                width: '15rem',
                                popoverIcon: true
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <span *ngIf="row.feeShare && row.feeShare > 0; else emptyFeeShare">
                                    {{ row.feeShare * 1.0 | lsqPercent }}
                                </span>
                                <ng-template #emptyFeeShare> ---- </ng-template>
                            </ng-template>
                        </lsq-column>

                        <lsq-currency-column
                            [column]="{
                                header: openInvoiceAmountI18n,
                                key: 'openInvoiceAmount',
                                headerPopoverContent: openInvoiceAmountHelpTextI18n,
                                sortable: true,
                                width: '15%'
                            }"
                        >
                        </lsq-currency-column>

                        <lsq-currency-column
                            [column]="{
                                header: earlyPaidAmountI18n,
                                key: 'earlyPaidAmount',
                                headerPopoverContent: earlyPaidAmountHelpTextI18n,
                                sortable: true,
                                width: '15%'
                            }"
                        >
                        </lsq-currency-column>

                        <lsq-percent-column
                            [isPercentWhole]="true"
                            [column]="{
                                header: percentUsedI18n,
                                key: 'percentUsed',
                                headerPopoverContent: percentUsedHelpTextI18n,
                                sortable: true,
                                width: '10rem'
                            }"
                        >
                        </lsq-percent-column>

                        <lsq-column
                            [column]="{
                                header: enrollmentStatusI18n,
                                key: 'enrollmentStatus',
                                className: 'status',
                                sortable: true,
                                width: '11rem'
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <status-pill
                                    *ngIf="row.enrollmentStatus"
                                    [status]="row.enrollmentStatus"
                                    [color]="statusColor[row.enrollmentStatus]"
                                    [displayValueMap]="supplierStatusDisplay"
                                    [width]="'10rem'"
                                >
                                </status-pill>
                            </ng-template>
                        </lsq-column>

                        <lsq-currency-column
                            [column]="{
                                header: flexTermAmountTotalI18n,
                                key: 'flexTermAmount',
                                headerPopoverContent: flexTermAmountTotalHelpTextI18n,
                                sortable: false,
                                width: '25%',
                                popoverIcon: true
                            }"
                        >
                        </lsq-currency-column>

                        <lsq-percent-column
                            [isPercentWhole]="true"
                            [column]="{
                                header: flexTermPercentUsedI18n,
                                key: 'flexTermPercentUsed',
                                sortable: false,
                                width: (responsiveService.smBelow | async) ? '40%' : '20%',
                                className: 'last-column',
                                headerPopoverContent: flexTermPercentUsedHelpTextI18n,
                                popoverIcon: true
                            }"
                        >
                        </lsq-percent-column>
                    </lsq-data-table>
                </div>
            </card>

            <lsq-data-table-mobile
                *ngIf="(responsiveService.mdBelow | async) && !!listSuppliersDataSource"
                [dataSource]="listSuppliersDataSource"
                [isPaginated]="true"
            >
                <ng-template #mobileCardHeader let-row="row">
                    <div class="mtc-header-content">
                        <div class="mtc-header-title">
                            <a
                                *ngIf="supplierDetailsIsOn$ | async"
                                [routerLink]="getSupplierDetailsLink(row.sellerKey)"
                                queryParamsHandling="preserve"
                            >
                                {{ row.name }}
                            </a>
                            <span *ngIf="!(supplierDetailsIsOn$ | async)">
                                {{ row.name }}
                            </span>
                        </div>
                        <div class="mtc-header-sub-title">
                            <div [style.padding]="'0.5rem 0'">
                                {{ row.buyerName }}
                            </div>
                            <div>
                                <status-pill
                                    *ngIf="row.enrollmentStatus"
                                    [status]="row.enrollmentStatus"
                                    [color]="statusColor[row.enrollmentStatus]"
                                    [displayValueMap]="supplierStatusDisplay"
                                    [width]="'10rem'"
                                >
                                </status-pill>
                            </div>
                        </div>
                    </div>
                </ng-template>

                <ng-template #mobileCardColumns let-row="row">
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ openInvoiceAmountI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.openInvoiceAmount | lsqCurrency }}
                        </div>
                    </div>

                    <div class="card-item">
                        <div class="card-item-label">
                            {{ earlyPaidAmountI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.earlyPaidAmount | lsqCurrency }}
                        </div>
                    </div>

                    <div class="card-item">
                        <div class="card-item-label">
                            {{ percentUsedI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.percentUsed | lsqPercent }}
                        </div>
                    </div>

                    <div class="card-item" *ngIf="buyerPaidEarlyPaymentFeePercentageVisible">
                        <div class="card-item-label">
                            {{ feeShareI18n }}
                        </div>
                        <div class="card-item-value">
                            <span *ngIf="row.feeShare && row.feeShare > 0; else emptyFeeShare">
                                {{ row.feeShare }}%
                            </span>
                            <ng-template #emptyFeeShare> ---- </ng-template>
                        </div>
                    </div>

                    <div class="card-item">
                        <div class="card-item-label">
                            {{ flexTermAmountTotalI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.flexTermAmount | lsqCurrency }}
                        </div>
                    </div>

                    <div class="card-item">
                        <div class="card-item-label">
                            {{ flexTermPercentUsedI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.flexTermPercentUsed | lsqPercent }}
                        </div>
                    </div>
                </ng-template>
            </lsq-data-table-mobile>
        </div>
    `,
    styleUrls: ['./suppliers.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AccountSuppliersComponent implements OnInit {
    public isFinancingAccountPortalUser = false;
    public supplierDetailsIsOn$ = this._featureFlagService.isOn(
        FeatureFlags.SupplierDetailsForBuyers
    );
    public filtersFormGroup = this._suppliersDataSourceService.filtersFormGroup;

    public supplierSummaryDataSource!: SupplierOverviewDataSource;
    public supplierSummaryDataPoints$!: Observable<LsqBarDataPoint[]>;
    public listSuppliersDataSource: LsqApiServiceListSource<
        any,
        any,
        ListSellerSummaryForBuyerAccountResponse['rows'][0]
    > | null = null;

    public readonly supplierIdTruncateAfter$ = this._windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 24;
            }
            if (dimensions.width > 1100) {
                return 14;
            }
            return 10;
        })
    );

    public readonly sellerEnrollmentStatusOptions: ISelectOption[] = [
        {
            label: this._i18n('Enrolled'),
            value: SellerSummaryEnrollmentStatus.ENROLLED
        },
        {
            label: this._i18n('Not Enrolled'),
            value: SellerSummaryEnrollmentStatus.NOT_ENROLLED
        },
        {
            label: this._i18n('Ineligible'),
            value: SellerSummaryEnrollmentStatus.INELIGIBLE
        }
    ];

    public readonly tableAppearance = TableAppearance;
    public readonly color = Color;
    public readonly icons = Icons;
    public readonly statusColor = StatusColor as object as Record<any, Color>;
    public readonly reportName = `Suppliers_${this._lsqDatePipe
        .transform(new Date(), 'shortDate')
        .replace(/\//g, '-')}`;
    public readonly statusText = {
        ENROLLED: this._i18n({
            value: 'Enrolled',
            meaning: 'The supplier is enrolled in LSQ FastTrack',
            description: 'The enrollment status of a supplier - Table Cell'
        }),
        NOT_ENROLLED: this._i18n({
            value: 'Not Enrolled',
            meaning: 'The supplier is not enrolled in LSQ FastTrack',
            description: 'The enrollment status of a supplier - Table Cell'
        }),
        INELIGIBLE: this._i18n({
            value: 'Ineligible',
            meaning: 'The supplier is not eligible for LSQ FastTrack',
            description: 'The enrollment status of a supplier - Table Cell'
        })
    };
    public readonly yourSuppliersI18n = this._i18n({
        value: 'Your Suppliers',
        meaning: `Your (the buyer's) suppliers`,
        description: 'Page title'
    });
    public readonly businessUnitI8n = this._i18n({
        value: 'Business Unit',
        description: 'Table column header'
    });
    public readonly supplierIdI8n = this._i18n({
        value: 'Supplier ID',
        description: 'Table column header'
    });
    public readonly supplierNameI18n = this._i18n({
        value: 'Supplier',
        description: 'Table column header'
    });

    public feeShareI18n = this._i18n({
        value: 'Fee share',
        description: 'Table column header'
    });

    public feeSharePopoverI18n = this._i18n({
        value: "The displayed shared fee rate is the percentage you are paying towards your supplier's early payment fees.",
        description: 'Popover column header'
    });

    public readonly openInvoiceAmountI18n = this._i18n({
        value: 'Invoice Total',
        description: 'Table column header'
    });
    public readonly openInvoiceAmountHelpTextI18n = this._i18n({
        value: 'The total dollar amount of invoices open at this time. Note: this includes all ineligible invoices.',
        description: 'Table column header help text'
    });
    public readonly earlyPaidAmountI18n = this._i18n({
        value: 'Early Paid Total',
        meaning: `The total amount of the supplier's invoices paid by LSQ but not yet collected from the buyer`,
        description: 'Table column header'
    });
    public readonly earlyPaidAmountHelpTextI18n = this._i18n({
        value: 'The total dollar amount of invoices open and early paid at this time',
        description: 'Table column header help text'
    });
    public readonly percentUsedI18n = this._i18n({
        value: '% Used',
        meaning: `The early paid total as a percentage of the open invoice total`,
        description: 'Table column header'
    });
    public readonly percentUsedHelpTextI18n = this._i18n({
        value: 'The percentage of the amount early paid out of the total invoice amount open at this time',
        description: 'Table column header help text'
    });
    public readonly enrollmentStatusI18n = this._i18n({
        value: 'Status',
        meaning: `The enrollment status of a supplier`,
        description: 'Table column header'
    });
    public readonly enrollmentStatusMultiSelectPlaceholderI18n = this._i18n({
        value: 'Select Enrollment Status',
        description: 'Multi-select dropdown label'
    });
    public readonly allEnrollmentStatusesSelectedI18n = this._i18n({
        value: 'All Enrollment Statuses',
        description: 'Multi-select dropdown label'
    });
    public readonly searchSuppliersPlaceholderI18n = this._i18n({
        value: 'Search Suppliers',
        description: 'Search box placeholder'
    });
    public flexTermPercentUsedI18n = this._i18n({
        value: '% FlexTerm Used',
        description: 'Table column header'
    });
    public flexTermPercentUsedHelpTextI18n = this._i18n({
        value: 'The percentage of the amount from FlexTerm invoices out of the facility limit amount.',
        description: 'Table column header help text'
    });
    public flexTermAmountTotalI18n = this._i18n({
        value: 'FlexTerm Total',
        description: 'Table column header'
    });
    public flexTermAmountTotalHelpTextI18n = this._i18n({
        value: 'Total amount from invoices requested for FlexTerm.',
        description: 'Table column header help text'
    });

    public buyerPaidEarlyPaymentFeePercentageVisible: boolean;

    constructor(
        public responsiveService: ResponsiveService,
        public listSellerSummaryExportService: ListSellerSummaryForBuyerAccountExportService,
        public listSellerSummaryForBuyerExportService: ListSellerSummaryExportService,
        public supplierStatusDisplay: SupplierStatusDisplay,
        private _i18n: I18n,
        private _lsqDatePipe: LsqDatePipe,
        private _windowReferenceService: WindowReferenceService,
        private _supplyChainDirectAccountService: SupplyChainDirectAccountService,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _suppliersDataSourceService: SuppliersDataSourceService,
        private _supplierOverviewDataSourceFactory: SupplierOverviewDataSourceFactory,
        private _claimService: ClaimService,
        private _featureFlagService: FeatureFlagService,
        private _decimalPipe: DecimalPipe
    ) {}

    public getSupplierDetailsLink(sellerKey: string): string {
        return getDynamicRouteLink(
            getRouterLinkUrlById(
                atlas,
                this.isFinancingAccountPortalUser
                    ? ACCOUNT_SUPPLIER_DETAILS
                    : BUYER_SUPPLIER_DETAILS
            ),
            { sellerKey }
        );
    }

    public ngOnInit(): void {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
        this.buyerAccountSharedService.isBuyerPaidEarlyPaymentFeePercentageShown$
            .pipe(take(1))
            .toPromise()
            .then((buyerPaidEarlyPaymentFeePercentageVisible) => {
                this.buyerPaidEarlyPaymentFeePercentageVisible =
                    buyerPaidEarlyPaymentFeePercentageVisible;
                this._suppliersDataSourceService.initializeSuppliersListSource(
                    this.buyerPaidEarlyPaymentFeePercentageVisible
                );
                this.listSuppliersDataSource =
                    this._suppliersDataSourceService.listSuppliersDataSource;

                // Dispatch new requests whenever the `buyerKeys` filter changes, and
                // reset the suppliers table page to 1.
                this._supplyChainDirectAccountService.buyerKeysFormGroup
                    .get('buyerKeys')
                    ?.valueChanges.pipe(forLifeOf(this))
                    .subscribe((buyerKeysFilter) => {
                        const currentRequest =
                            this.listSuppliersDataSource?.request ??
                            new ListSellerSummaryForBuyerAccountRequest();
                        const currentPagination = currentRequest.pagination ?? new Pagination();
                        this.listSuppliersDataSource?.dispatchRequest({
                            ...currentRequest,
                            buyerKeysFilter,
                            pagination: {
                                ...currentPagination,
                                skip: 0
                            }
                        });
                        this.supplierSummaryDataSource.dispatchRequest({
                            buyerKeys: buyerKeysFilter
                        });
                    });

                // Dispatch a new request for suppliers whenever the `sellerNameFilter` changes, and
                // reset the page to 1.
                this._suppliersDataSourceService.filtersFormGroup
                    .get('sellerNameFilter')
                    ?.valueChanges.pipe(
                        forLifeOf(this),
                        debounceTime(Interaction.SearchDebounceTimeMs)
                    )
                    .subscribe((sellerNameFilter) => {
                        const currentRequest =
                            this.listSuppliersDataSource?.request ??
                            new ListSellerSummaryForBuyerAccountRequest();
                        const currentPagination = currentRequest.pagination ?? new Pagination();
                        this.listSuppliersDataSource?.dispatchRequest({
                            ...currentRequest,
                            sellerNameFilter,
                            pagination: {
                                ...currentPagination,
                                skip: 0
                            }
                        });
                    });

                // Dispatch a new request for suppliers whenever the `sellerEnrollmentStatusFilter` changes, and
                // reset the page to 1.
                this._suppliersDataSourceService.filtersFormGroup
                    .get('sellerEnrollmentStatusFilter')
                    ?.valueChanges.pipe(forLifeOf(this))
                    .subscribe((sellerEnrollmentStatusFilter) => {
                        const currentRequest =
                            this.listSuppliersDataSource?.request ??
                            new ListSellerSummaryForBuyerAccountRequest();
                        const currentPagination = currentRequest.pagination ?? new Pagination();
                        this.listSuppliersDataSource?.dispatchRequest({
                            ...currentRequest,
                            sellerEnrollmentStatusFilter,
                            pagination: {
                                ...currentPagination,
                                skip: 0
                            }
                        });
                    });

                this._suppliersDataSourceService.filtersFormGroup
                    .get('sellerEnrollmentStatusFilter')
                    ?.setValue([]);
            });

        this.supplierSummaryDataSource =
            this._supplierOverviewDataSourceFactory.createSupplierOverviewDataSource({
                buyerKeys:
                    this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                        ?.value ?? []
            });
        this.supplierSummaryDataPoints$ = this.supplierSummaryDataSource.data$.pipe(
            forLifeOf(this),
            ignoreNullish(),
            map((supplierSummary) => [
                {
                    label: this._i18n('Enrolled'),
                    data: this._decimalPipe.transform(supplierSummary.enrolledCount) ?? '0'
                },
                {
                    label: this._i18n('Not Enrolled'),
                    data: this._decimalPipe.transform(supplierSummary.notEnrolledCount) ?? '0'
                },
                {
                    label: this._i18n('Invitations Sent'),
                    data:
                        this._decimalPipe.transform(supplierSummary.notEnrolledInvitedCount) ?? '0'
                },
                {
                    label: this._i18n('Total Suppliers'),
                    data: this._decimalPipe.transform(supplierSummary.totalCount) ?? '0'
                }
            ])
        );
    }
}
